import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PageBody } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { NotFoundPage } from '@/components/NotFoundPage.tsx';
import { MeetingView } from '@/components/meetings/MeetingView.tsx';
import type { MeetingPanel } from '@/components/meetings/meetingPanel.ts';
import { useMeetingChatOffer } from '@/components/meetings/useMeetingChatOffer.ts';
import { usePublishChatAnchor } from '@/lib/chatAnchor.ts';
import {
  chatDrawerFitsBesidePage,
  openChatDrawerForPage,
} from '@/lib/chatDrawerState.ts';
import { usePublishChatContentMin } from '@/lib/chatContentMin.ts';
import { loadOrNotFound } from '@/lib/loadOrNotFound.ts';
import { meetingPageQuery } from '@/lib/meetingPageQuery.ts';
import { m } from '@/paraglide/messages.js';

/*
 * How narrow this page's column may get before the chat has to lie over it.
 *
 * Well under the 960px other pages hold out for: a meeting is read a section at
 * a time and is legible in a narrow column, and it is the one page people bring
 * questions about — so the chat standing beside it matters more here than the
 * page keeping its full width. This is what lets the two share an ordinary
 * laptop screen.
 */
const MEETING_CONTENT_MIN = 600;

/*
 * Which document the link is about. A verslag notification says `tab=report`
 * so the reader lands on the verslag it announced; anything else means the
 * page opens as always, on the preparation.
 */
interface MeetingSearch {
  tab?: 'report';
}

export const Route = createFileRoute('/_app/meetings/$meetingId')({
  validateSearch: (search: Record<string, unknown>): MeetingSearch => ({
    tab: search.tab === 'report' ? 'report' : undefined,
  }),
  loader: ({ context, params }) =>
    loadOrNotFound(async () => {
      const user = await context.queryClient.ensureQueryData({
        queryKey: ['currentUser'],
        queryFn: fetchCurrentUser,
      });
      await context.queryClient.ensureQueryData(
        meetingPageQuery(user.id, params.meetingId),
      );
    }),
  /* Shown straight away, as the view's own loading line was before the loader. */
  pendingComponent: MeetingPending,
  pendingMs: 0,
  pendingMinMs: 0,
  notFoundComponent: NotFoundPage,
  component: MeetingRoute,
});

function MeetingPending() {
  return (
    <PageBody flush>
      <p className='text-base text-ink-muted'>{m.meeting_loading()}</p>
    </PageBody>
  );
}

function MeetingRoute() {
  const { meetingId } = Route.useParams();
  const { tab } = Route.useSearch();
  /*
   * Keyed by the meeting and the linked tab: another meeting, or a link to the
   * verslag of the open one (back and forward too), starts the page afresh.
   */
  return (
    <MeetingDetailPage
      key={`${meetingId}:${tab ?? 'prep'}`}
      meetingId={meetingId}
      initialPanel={tab}
    />
  );
}

export interface MeetingDetailPageProps {
  meetingId: string;
  initialPanel?: MeetingPanel;
}

/**
 * One meeting's page: the view, plus the two things that only a page has — a
 * heading, and the way back to the week it came from.
 *
 * Everything else is `MeetingView`, which is the whole of what a meeting is.
 */
export function MeetingDetailPage({ meetingId, initialPanel }: MeetingDetailPageProps) {
  /*
   * What the chat is sitting beside while this page is open. Said here rather
   * than in the drawer: the page is the one thing that knows which meeting it
   * is, and it stops being true the moment the user leaves.
   */
  usePublishChatAnchor({ type: 'meeting', id: meetingId });

  /*
   * Said before the chat is asked whether it fits: the answer depends on this,
   * and effects run in the order they are written.
   */
  usePublishChatContentMin(MEETING_CONTENT_MIN);

  /*
   * The one page that opens the chat rather than waiting to be asked: a meeting
   * is what people bring questions about, and the drawer already knows which
   * one it is sitting beside.
   *
   * Only for as long as the page is on screen, and only where it fits beside
   * the page — narrower than the shell's drawer track it would lie over the
   * meeting the user just opened. Once per meeting, so closing it here keeps it
   * closed until another meeting is opened.
   */
  useEffect(() => {
    /*
     * Its own floor, said outright rather than read back from the publish
     * above: the answer must not depend on whose copy of that module answers.
     */
    if (!chatDrawerFitsBesidePage(MEETING_CONTENT_MIN)) return;

    return openChatDrawerForPage();
  }, [meetingId]);

  /* Which tab is open: the chat beside the page is about the same thing. */
  const [panel, setPanel] = useState<MeetingPanel>(initialPanel ?? 'prep');
  const navigate = useNavigate();

  /* What this meeting puts in the drawer beside it, tab by tab. */
  useMeetingChatOffer(meetingId, panel);

  /*
   * No title above the body: the meeting's hero bar is this page's heading, and
   * the way back sits under it. Hence `flush` — the room a title would need is
   * room this page does not want.
   */
  return (
    <PageBody flush>
      <MeetingView
        meetingId={meetingId}
        panel={panel}
        onPanelChange={setPanel}
        /* Deleted from its own page: there is nothing here to come back to. */
        onLeave={() => void navigate({ to: '/' })}
      />
    </PageBody>
  );
}
